import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/apps/core'
import type { RelationshipKind } from '@/api/assets/uploads'
import { useUploadStore, MAX_QUEUE_SIZE } from '../stores/useUploadStore'
import type { UploadItem, UploadMetadata, UploadProgress } from '../types/upload.types'

export interface UseUploadOptions {
  workspaceId?: MaybeRefOrGetter<string | undefined>
  parentFolderId?: MaybeRefOrGetter<string | undefined>
  autoOpenDrawer?: boolean
}

export interface UseUploadReturn {
  addFiles: (files: File[]) => string[]
  replaceAssetFile: (file: File, targetAssetId: string, workspaceId?: string) => string | null
  addRelatedFiles: (
    files: File[],
    targetAssetId: string,
    relationshipKind: RelationshipKind,
    workspaceId?: string,
    /** Offer the new asset as a linked file on the target's share page and in its player. */
    isPublic?: boolean,
  ) => string[]
  pauseUpload: (id: string) => void
  resumeUpload: (id: string) => void
  cancelUpload: (id: string) => void
  retryUpload: (id: string) => void
  removeUpload: (id: string) => void
  clearCompleted: () => void
  openDrawer: () => void
  closeDrawer: () => void
  hasActiveUploads: ComputedRef<boolean>
  overallProgress: UploadProgress
  /** Get upload item by ID */
  getUpload: (id: string) => UploadItem | undefined
  /** Wait for asset_uid to become available (resolves before upload completes) */
  waitForAssetUid: (id: string, timeoutMs?: number) => Promise<string>
}

/**
 * Composable for managing asset file uploads.
 *
 * Provides a simplified API for adding files to the upload queue and managing
 * upload state. Automatically opens the upload drawer when files are added
 * unless disabled via options.
 *
 * The workspaceId and parentFolderId are read from the route parameters when not given:
 * - workspaceId from `:workspaceId`
 * - parentFolderId from `:folderId`
 *
 * The organization is the one in the URL, which the router already sends as `X-Org-Id`.
 *
 * @param options - Configuration options for uploads
 * @returns Upload management functions and reactive state
 *
 * @example
 * ```typescript
 * // IDs are auto-detected from route
 * const { addFiles, hasActiveUploads, overallProgress } = useUpload({
 *   autoOpenDrawer: true,
 * })
 *
 * // Or explicitly provide IDs
 * const { addFiles } = useUpload({
 *   workspaceId: 'workspace-456',
 *   parentFolderId: 'folder-789',
 * })
 *
 * // Add files from input or drop event
 * function handleDrop(event: DragEvent) {
 *   const files = Array.from(event.dataTransfer?.files ?? [])
 *   addFiles(files)
 * }
 * ```
 */
export function useUpload(options: UseUploadOptions = {}): UseUploadReturn {
  const store = useUploadStore()
  const route = useRoute()
  const toast = useToast()
  const { t } = useI18n()

  const { workspaceId: explicitWorkspaceId, parentFolderId: explicitFolderId, autoOpenDrawer = true } = options

  function enforceQueueLimit(files: File[]): File[] {
    const slots = store.availableSlots
    if (files.length <= slots) return files

    if (slots === 0) {
      toast.error(
        t('assets.upload.errors.queueFull', { max: MAX_QUEUE_SIZE }),
      )
    } else {
      toast.error(
        t('assets.upload.errors.queueWouldExceed', { max: MAX_QUEUE_SIZE }),
      )
    }
    return []
  }

  // An optional param the URL leaves out reads as `''` — a workspace's root has no `folderId` —
  // and an empty id is not "none" to the server, which refuses it as a malformed UUID.
  function getRouteParam(paramName: string): string | undefined {
    const value = route.params[paramName]
    if (typeof value === 'string' && value !== '') return value
    return undefined
  }

  function getWorkspaceId(): string | undefined {
    return toValue(explicitWorkspaceId) ?? getRouteParam('workspaceId')
  }

  function getParentFolderId(): string | undefined {
    return toValue(explicitFolderId) ?? getRouteParam('folderId')
  }

  function addFiles(files: File[]): string[] {
    const workspaceId = getWorkspaceId()

    if (!workspaceId) {
      console.error('useUpload: workspaceId is required')
      return []
    }

    const allowed = enforceQueueLimit(Array.from(files))
    if (allowed.length === 0) return []

    const metadata: Partial<UploadMetadata> = {
      workspaceId,
      parentFolderId: getParentFolderId(),
    }

    const ids = store.addFiles(allowed, metadata)

    if (autoOpenDrawer && ids.length > 0) {
      store.openDrawerAutomatically()
    }

    return ids
  }

  // The two uploads against an asset still carry the workspace they were started from: the
  // drawer groups by it, though the server acts in the target's own and is not told this one.
  function replaceAssetFile(file: File, targetAssetId: string, overrideWorkspaceId?: string): string | null {
    const workspaceId = overrideWorkspaceId ?? getWorkspaceId()

    if (!workspaceId) {
      console.error('useUpload: workspaceId is required')
      return null
    }

    const allowed = enforceQueueLimit([file])
    if (allowed.length === 0) return null

    const metadata: Partial<UploadMetadata> = {
      workspaceId,
      uploadMode: 'version',
      targetAssetId,
    }

    const ids = store.addFiles(allowed, metadata)

    if (autoOpenDrawer && ids.length > 0) {
      store.openDrawerAutomatically()
    }

    return ids[0] ?? null
  }

  function addRelatedFiles(
    files: File[],
    targetAssetId: string,
    relationshipKind: RelationshipKind,
    overrideWorkspaceId?: string,
    isPublic = false,
  ): string[] {
    const workspaceId = overrideWorkspaceId ?? getWorkspaceId()

    if (!workspaceId) {
      console.error('useUpload: workspaceId is required')
      return []
    }

    const allowed = enforceQueueLimit(Array.from(files))
    if (allowed.length === 0) return []

    const metadata: Partial<UploadMetadata> = {
      workspaceId,
      uploadMode: 'related',
      targetAssetId,
      relationshipKind,
      isPublic,
    }

    const ids = store.addFiles(allowed, metadata)

    if (autoOpenDrawer && ids.length > 0) {
      store.openDrawerAutomatically()
    }

    return ids
  }

  return {
    addFiles,
    replaceAssetFile,
    addRelatedFiles,
    pauseUpload: store.pauseUpload,
    resumeUpload: store.resumeUpload,
    cancelUpload: store.cancelUpload,
    retryUpload: store.retryUpload,
    removeUpload: store.removeUpload,
    clearCompleted: store.clearCompleted,
    openDrawer: store.openDrawer,
    closeDrawer: store.closeDrawer,
    hasActiveUploads: computed(() => store.hasActiveOrPendingUploads),
    overallProgress: store.overallProgress,
    getUpload: store.getUpload,
    waitForAssetUid: store.waitForAssetUid,
  }
}
